import {
    CreditCard,
    Facebook,
    Mail,
    MessageCircle,
    Phone,
    Smartphone,
    type LucideIcon,
} from 'lucide-react';
import type { IntegrationType, TenantIntegration } from '@/api/integrations/integrationService';

/**
 * Single catalog of the channels, providers and credential fields the Integrations
 * page offers. Mirrors `App\Services\Integration\IntegrationSecrets` in quantix-api
 * (`SECRET_KEYS` below) and the provider validation rules of the backend.
 *
 * Every user-facing string is an i18n key in the `crm` namespace.
 */

export type IntegrationChannel = IntegrationType;
export type IntegrationGroup = 'communication' | 'marketing';
export type FieldType = 'text' | 'password' | 'email' | 'number' | 'select' | 'url';
/** Form sections, in display order. `environment` renders as a segmented control. */
export type FieldSection = 'environment' | 'credentials' | 'sender' | 'options';

export const FIELD_SECTIONS: readonly FieldSection[] = [
    'environment',
    'credentials',
    'sender',
    'options',
];

/**
 * Configuration keys the API masks in every response and never needs echoed back
 * (contract: "Secret masking"). Exactly the fields with `secret: true`.
 */
export const SECRET_KEYS: readonly string[] = [
    'auth_token',
    'secret',
    'api_token',
    'api_key',
    'password',
    'access_token',
    'webhook_verify_token',
    'api_secret',
    'webhook_secret',
    'merchant_key',
    'merchant_secret',
    'merchant_api_secret',
    'app_secret',
    'system_user_token',
];

export interface CatalogField {
    key: string;
    /** i18n key (namespace `crm`). */
    label: string;
    type: FieldType;
    required: boolean;
    /** i18n key of the one-line help under the input. */
    help: string;
    /** Allowed values for `type: 'select'`; labels come from `integrations.options.<value>`. */
    options?: string[];
    /** True for the keys in `SECRET_KEYS`: masked by the API, shown read-only with Replace. */
    secret?: boolean;
    section: FieldSection;
    /** Pre-filled for a new provider. */
    defaultValue?: string;
    /** Literal example, not translated. */
    placeholder?: string;
}

export interface CatalogProvider {
    id: string;
    /** Brand name, not translated. */
    name: string;
    /** i18n key of the one-line description shown on the provider card. */
    description: string;
    fields: CatalogField[];
    /** False when the backend has no credentials-only check for this provider (nothing is persisted). */
    checkSupported?: boolean;
    /** Non-secret detail for the card line, e.g. the sender address or number. */
    detail?: (configuration: Record<string, any>) => string | undefined;
}

export interface CatalogChannel {
    id: IntegrationChannel;
    group: IntegrationGroup;
    icon: LucideIcon;
    /** i18n keys. */
    name: string;
    description: string;
    providers: CatalogProvider[];
}

interface FieldOptions {
    required?: boolean;
    options?: string[];
    /** Use the shared `integrations.fields.shared.<key>` strings instead of provider-specific ones. */
    shared?: boolean;
    defaultValue?: string;
    placeholder?: string;
}

const str = (value: unknown): string | undefined =>
    value === undefined || value === null || String(value).trim() === ''
        ? undefined
        : String(value).trim();

function makeField(
    scope: string,
    key: string,
    type: FieldType,
    section: FieldSection,
    opts: FieldOptions = {}
): CatalogField {
    const base = opts.shared ? 'integrations.fields.shared' : `integrations.fields.${scope}`;
    const field: CatalogField = {
        key,
        label: `${base}.${key}.label`,
        type,
        required: opts.required ?? false,
        help: `${base}.${key}.help`,
        section,
    };
    if (opts.options) field.options = opts.options;
    if (SECRET_KEYS.includes(key)) field.secret = true;
    if (opts.defaultValue !== undefined) field.defaultValue = opts.defaultValue;
    if (opts.placeholder) field.placeholder = opts.placeholder;
    return field;
}

function makeProvider(
    channel: IntegrationChannel,
    id: string,
    name: string,
    build: (f: ReturnType<typeof scoped>) => CatalogField[],
    detail?: CatalogProvider['detail'],
    checkSupported = true
): CatalogProvider {
    const scope = `${channel}.${id}`;
    return {
        id,
        name,
        description: `integrations.providers.${scope}.description`,
        fields: build(scoped(scope)),
        detail,
        checkSupported,
    };
}

const scoped =
    (scope: string) =>
    (key: string, type: FieldType, section: FieldSection, opts: FieldOptions = {}) =>
        makeField(scope, key, type, section, opts);

const ENVIRONMENT_OPTIONS = ['sandbox', 'live'];

export const INTEGRATION_CATALOG: CatalogChannel[] = [
    {
        id: 'voip',
        group: 'communication',
        icon: Phone,
        name: 'integrations.channels.voip.name',
        description: 'integrations.channels.voip.description',
        providers: [
            makeProvider(
                'voip',
                'twilio',
                'Twilio',
                f => [
                    f('account_sid', 'text', 'credentials', { required: true, shared: true }),
                    f('auth_token', 'password', 'credentials', { required: true, shared: true }),
                    f('phone_number', 'text', 'sender', {
                        required: true,
                        placeholder: '+14155550100',
                    }),
                ],
                c => str(c.phone_number)
            ),
            makeProvider(
                'voip',
                'arestik',
                'Arestik PBX',
                f => [
                    f('host', 'text', 'credentials', { required: true }),
                    f('port', 'number', 'credentials', { placeholder: '8088' }),
                    f('username', 'text', 'credentials', { required: true }),
                    f('secret', 'password', 'credentials', { required: true }),
                    f('channel_prefix', 'select', 'options', {
                        options: ['PJSIP', 'SIP'],
                        defaultValue: 'PJSIP',
                    }),
                    f('ari_context', 'text', 'options'),
                    f('outbound_prefix', 'text', 'options', { placeholder: '9' }),
                    f('tls', 'select', 'options', {
                        options: ['false', 'true'],
                        defaultValue: 'false',
                    }),
                ],
                c => {
                    const host = str(c.host);
                    if (!host) return undefined;
                    const port = str(c.port);
                    return port ? `${host}:${port}` : host;
                }
            ),
            makeProvider(
                'voip',
                'voiceflow_c2c',
                'VoiceFlow C2C',
                f => [
                    f('api_token', 'password', 'credentials', { required: true }),
                    f('callback_url', 'url', 'options', { placeholder: 'https://' }),
                    f('brand', 'text', 'options'),
                ],
                c => str(c.brand) ?? str(c.callback_url),
                false
            ),
        ],
    },
    {
        id: 'email',
        group: 'communication',
        icon: Mail,
        name: 'integrations.channels.email.name',
        description: 'integrations.channels.email.description',
        providers: [
            makeProvider(
                'email',
                'sendgrid',
                'SendGrid',
                f => [
                    f('api_key', 'password', 'credentials', { required: true }),
                    f('from_email', 'email', 'sender', { required: true, shared: true }),
                    f('from_name', 'text', 'sender', { shared: true }),
                ],
                c => str(c.from_email)
            ),
            makeProvider(
                'email',
                'mailchimp',
                'Mailchimp',
                f => [
                    f('api_key', 'password', 'credentials', { required: true }),
                    f('list_id', 'text', 'credentials', { required: true }),
                    f('from_email', 'email', 'sender', { required: true, shared: true }),
                    f('from_name', 'text', 'sender', { shared: true }),
                ],
                c => str(c.from_email),
                false
            ),
            makeProvider(
                'email',
                'smtp',
                'SMTP',
                f => [
                    f('host', 'text', 'credentials', { required: true }),
                    f('port', 'number', 'credentials', { required: true, placeholder: '587' }),
                    f('username', 'text', 'credentials', { required: true }),
                    f('password', 'password', 'credentials', { required: true }),
                    f('encryption', 'select', 'credentials', {
                        required: true,
                        options: ['tls', 'ssl'],
                        defaultValue: 'tls',
                    }),
                    f('from_email', 'email', 'sender', { required: true, shared: true }),
                    f('from_name', 'text', 'sender', { shared: true }),
                ],
                c => str(c.from_email) ?? str(c.host)
            ),
        ],
    },
    {
        id: 'whatsapp',
        group: 'communication',
        icon: MessageCircle,
        name: 'integrations.channels.whatsapp.name',
        description: 'integrations.channels.whatsapp.description',
        providers: [
            makeProvider(
                'whatsapp',
                'meta',
                'Meta WhatsApp',
                f => [
                    f('access_token', 'password', 'credentials', { required: true }),
                    f('webhook_verify_token', 'password', 'credentials', { required: true }),
                    f('business_account_id', 'text', 'credentials', { required: true }),
                    f('phone_number_id', 'text', 'sender', { required: true }),
                ],
                c => str(c.phone_number_id)
            ),
            makeProvider(
                'whatsapp',
                'twilio',
                'Twilio',
                f => [
                    f('account_sid', 'text', 'credentials', { required: true, shared: true }),
                    f('auth_token', 'password', 'credentials', { required: true, shared: true }),
                    f('whatsapp_number', 'text', 'sender', {
                        required: true,
                        placeholder: '+14155550100',
                    }),
                ],
                c => str(c.whatsapp_number)
            ),
            makeProvider(
                'whatsapp',
                'messagebird',
                'MessageBird',
                f => [
                    f('api_key', 'password', 'credentials', { required: true }),
                    f('channel_id', 'text', 'credentials', { required: true }),
                    f('phone_number', 'text', 'sender', {
                        required: true,
                        placeholder: '+14155550100',
                    }),
                ],
                c => str(c.phone_number),
                false
            ),
        ],
    },
    {
        id: 'sms',
        group: 'communication',
        icon: Smartphone,
        name: 'integrations.channels.sms.name',
        description: 'integrations.channels.sms.description',
        providers: [
            makeProvider(
                'sms',
                'twilio',
                'Twilio',
                f => [
                    f('account_sid', 'text', 'credentials', { required: true, shared: true }),
                    f('auth_token', 'password', 'credentials', { required: true, shared: true }),
                    f('phone_number', 'text', 'sender', {
                        required: true,
                        placeholder: '+14155550100',
                    }),
                ],
                c => str(c.phone_number)
            ),
        ],
    },
    {
        id: 'facebook',
        group: 'marketing',
        icon: Facebook,
        name: 'integrations.channels.facebook.name',
        description: 'integrations.channels.facebook.description',
        providers: [
            makeProvider(
                'facebook',
                'meta',
                'Meta',
                f => [
                    f('app_id', 'text', 'credentials', { required: true }),
                    f('app_secret', 'password', 'credentials', { required: true }),
                    f('system_user_token', 'password', 'credentials'),
                ],
                c => str(c.app_id)
            ),
        ],
    },
    {
        id: 'payment',
        group: 'marketing',
        icon: CreditCard,
        name: 'integrations.channels.payment.name',
        description: 'integrations.channels.payment.description',
        providers: [
            makeProvider(
                'payment',
                'coingate',
                'CoinGate',
                f => [
                    f('environment', 'select', 'environment', {
                        required: true,
                        shared: true,
                        options: ENVIRONMENT_OPTIONS,
                        defaultValue: 'sandbox',
                    }),
                    f('api_key', 'password', 'credentials', { required: true }),
                    f('api_secret', 'password', 'credentials', { required: true }),
                    f('webhook_secret', 'password', 'credentials', { shared: true }),
                ],
                () => undefined
            ),
            makeProvider(
                'payment',
                'pgpay',
                'PGPay',
                f => [
                    f('environment', 'select', 'environment', {
                        required: true,
                        shared: true,
                        options: ENVIRONMENT_OPTIONS,
                        defaultValue: 'sandbox',
                    }),
                    f('merchant_code', 'text', 'credentials', { required: true }),
                    f('merchant_secret', 'password', 'credentials', { required: true }),
                    f('merchant_key', 'password', 'credentials'),
                    f('api_key', 'password', 'credentials'),
                ],
                c => str(c.merchant_code)
            ),
            makeProvider(
                'payment',
                'transak',
                'Transak',
                f => [
                    f('environment', 'select', 'environment', {
                        required: true,
                        shared: true,
                        options: ENVIRONMENT_OPTIONS,
                        defaultValue: 'sandbox',
                    }),
                    f('api_key', 'password', 'credentials', { required: true }),
                    f('api_secret', 'password', 'credentials', { required: true }),
                    f('webhook_secret', 'password', 'credentials', { shared: true }),
                ],
                () => undefined
            ),
            makeProvider(
                'payment',
                'paymesh',
                'PayMesh',
                f => [
                    f('environment', 'select', 'environment', {
                        required: true,
                        shared: true,
                        options: ENVIRONMENT_OPTIONS,
                        defaultValue: 'sandbox',
                    }),
                    f('merchant_id', 'text', 'credentials', { required: true }),
                    f('api_key', 'password', 'credentials', { required: true }),
                    f('merchant_api_secret', 'password', 'credentials', { required: true }),
                ],
                c => str(c.merchant_id),
                false
            ),
        ],
    },
];

/** Groups in display order, each with the channels it holds. */
export const INTEGRATION_GROUPS: { id: IntegrationGroup; label: string }[] = [
    { id: 'communication', label: 'integrations.groups.communication' },
    { id: 'marketing', label: 'integrations.groups.marketing' },
];

export const getChannel = (id: IntegrationChannel): CatalogChannel => {
    const channel = INTEGRATION_CATALOG.find(item => item.id === id);
    if (!channel) {
        throw new Error(`Unknown integration channel "${id}"`);
    }
    return channel;
};

export const getProvider = (
    channel: IntegrationChannel,
    providerId: string
): CatalogProvider | undefined =>
    INTEGRATION_CATALOG.find(item => item.id === channel)?.providers.find(
        provider => provider.id === providerId
    );

export const channelsInGroup = (group: IntegrationGroup): CatalogChannel[] =>
    INTEGRATION_CATALOG.filter(channel => channel.group === group);

/** `live` / `sandbox` of a payment configuration; anything but `live` is sandbox. */
export const environmentOf = (configuration: Record<string, any> | null | undefined) =>
    configuration?.environment === 'live' ? 'live' : 'sandbox';

type SummarySource = Pick<TenantIntegration, 'integration_type' | 'provider_name'> & {
    configuration?: Record<string, any> | null;
};

/**
 * One-line card detail built from non-secret fields only,
 * e.g. `SendGrid · hello@acme.com`, `Twilio · +14155550100`.
 */
export function summaryFor(integration: SummarySource): string {
    const provider = getProvider(integration.integration_type, integration.provider_name);
    const name = provider?.name ?? integration.provider_name;
    const detail = provider?.detail?.(integration.configuration ?? {});
    return detail ? `${name} · ${detail}` : name;
}

/** Fields of the "Send a test" dialog; these inputs are not stored. */
export interface TestField {
    key: string;
    /** i18n key. */
    label: string;
    type: 'text' | 'email' | 'number' | 'textarea';
    required: boolean;
    /** Literal example, not translated. */
    placeholder?: string;
}

const testField = (
    key: string,
    type: TestField['type'],
    required: boolean,
    placeholder?: string
): TestField => ({ key, label: `integrations.test.fields.${key}`, type, required, placeholder });

/** The inputs a real test send needs, per channel (voip also needs an extension on PBX providers). */
export function getTestFields(channel: IntegrationChannel, providerId: string): TestField[] {
    switch (channel) {
        case 'email':
            return [
                testField('to', 'email', true, 'test@example.com'),
                testField('subject', 'text', false),
                testField('content', 'textarea', false),
            ];
        case 'sms':
        case 'whatsapp':
            return [
                testField('phone_number', 'text', true, '+14155550100'),
                testField('message', 'textarea', false),
            ];
        case 'voip': {
            const fields = [
                testField('phone_number', 'text', true, '+14155550100'),
                testField('customer_id', 'text', false),
            ];
            if (providerId === 'arestik' || providerId === 'voiceflow_c2c') {
                fields.push(testField('user_extension', 'text', true, '100'));
            }
            return fields;
        }
        default:
            return [];
    }
}
